import React, { useState, useEffect, useMemo } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { LOGO_CROPPED, LOGO_WHITE } from '../lib/assets';

function LanguageToggle({ scrolled }) {
  const { language, setLanguage } = useLanguage();
  return (
    <button
      onClick={() => setLanguage(language === 'en' ? 'id' : 'en')}
      className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
        scrolled ? 'text-slate-600 hover:text-brand-green' : 'text-white/80 hover:text-white'
      }`}
      data-testid="language-toggle"
    >
      <Globe className="w-4 h-4" />
      {language === 'en' ? 'ID' : 'EN'}
    </button>
  );
}

export default function Navbar() {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let rafId;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setScrolled(window.scrollY > 20));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const navItems = useMemo(() => [
    { label: t('nav.products'), to: 'products' },
    { label: t('nav.operations'), to: 'operations' },
    { label: t('nav.markets'), to: 'markets' },
    { label: t('nav.logistics'), to: 'logistics' },
    { label: t('nav.certifications'), to: 'certifications' },
  ], [t]);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-slate-200'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <ScrollLink
            to="hero"
            smooth={true}
            duration={500}
            className="cursor-pointer flex-shrink-0"
            data-testid="navbar-logo"
          >
            <img
              src={scrolled ? LOGO_CROPPED : LOGO_WHITE}
              alt="Dedari Export"
              className={`w-auto transition-all duration-300 ${
                scrolled ? 'h-10' : 'h-14'
              }`}
            />
          </ScrollLink>

          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <ScrollLink
                key={item.to}
                to={item.to}
                smooth={true}
                duration={500}
                offset={-80}
                className={`text-xs font-semibold uppercase tracking-wider cursor-pointer transition-colors ${
                  scrolled ? 'text-slate-600 hover:text-brand-green' : 'text-white/80 hover:text-white'
                }`}
                data-testid={`nav-link-${item.to}`}
              >
                {item.label}
              </ScrollLink>
            ))}

            <LanguageToggle scrolled={scrolled} />

            <ScrollLink
              to="quote"
              smooth={true}
              duration={500}
              offset={-80}
              className="bg-brand-orange text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider cursor-pointer hover:bg-brand-orange-hover transition-all shadow-md hover:shadow-lg"
              data-testid="nav-cta-button"
            >
              {t('nav.requestQuote')}
            </ScrollLink>
          </div>

          <div className="flex items-center gap-3 lg:hidden">
            <LanguageToggle scrolled={scrolled} />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 ${scrolled ? 'text-slate-700' : 'text-white'}`}
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-1">
            {navItems.map((item) => (
              <ScrollLink
                key={item.to}
                to={item.to}
                smooth={true}
                duration={500}
                offset={-80}
                onClick={() => setIsOpen(false)}
                className="block py-3 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 uppercase tracking-wider cursor-pointer"
                data-testid={`mobile-nav-link-${item.to}`}
              >
                {item.label}
              </ScrollLink>
            ))}
            <ScrollLink
              to="quote"
              smooth={true}
              duration={500}
              offset={-80}
              onClick={() => setIsOpen(false)}
              className="block mt-4 bg-brand-orange text-white text-center py-3 px-4 text-sm font-bold uppercase tracking-wider cursor-pointer"
              data-testid="mobile-cta-button"
            >
              {t('nav.requestQuote')}
            </ScrollLink>
          </div>
        </div>
      )}
    </nav>
  );
}
